import { useMergeRefs } from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import { type FC, type HTMLProps, useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '#components/Icon'
import PlayingIcon from '#icons/chevron-1-right-24.svg'
import PauseIcon from '#icons/pause-24.svg'
import { style } from '#styles/index'

const videoClass = css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  > .video {
    width: 100%;
  }
  
  > .play-indicator {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;

    display: flex;
    align-items: center;
    justify-content: center;
    
    background-color: color-mix(in srgb, ${style.theme.backgroundSurfaceSecondary} 20%, transparent 80%);

    transition: 0.25s ease opacity;
    &:hover {
      opacity: 0.2;
    }

    &.stopped {
      opacity: 1.0;
    }

    &.playing {
      opacity: 0.0;
      > .icon {
        color: transparent;
      }
    }

    &.paused {
      opacity: 1.0;
      > .icon {
        animation: ease-in-out 1s infinite alternate pulsing;

        @keyframes pulsing {
          from { opacity: 1.0; }
          to { opacity: 0.8; }
        }
      }
    }

    > .icon {
      color: ${style.theme.textAccent}
    }
  }
`

const isPlaying = (video: HTMLVideoElement) => !!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2)

export const Video: FC<
  Omit<HTMLProps<HTMLVideoElement>, 'onClick'> &
    Pick<HTMLProps<HTMLDivElement>, 'onClick'> &
    (
      | {
          playState: 'playing' | 'paused' | 'stopped'
          setPlayState: (playState: 'playing' | 'paused' | 'stopped') => void
        }
      | { playState: undefined; setPlayState: undefined }
    )
> = ({ ref, src, className, onClick, autoPlay, playState: externalPlayState, setPlayState: setExternalPlaystate, ...rest }) => {
  const internalRef = useRef<HTMLVideoElement>(null)
  const mergedRefs = useMergeRefs([ref, internalRef])
  const [internalPlayState, setInternalPlayState] = useState<'playing' | 'paused' | 'stopped'>(autoPlay ? 'playing' : 'stopped')
  const [playState, setPlayState] = externalPlayState ? [externalPlayState, setExternalPlaystate] : [internalPlayState, setInternalPlayState]
  const onClickInternal = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!internalRef.current) {
        onClick?.(e)
        return
      }
      const videoElement = internalRef.current

      if (isPlaying(videoElement)) {
        setPlayState('paused')
      } else {
        setPlayState('playing')
      }
      onClick?.(e)
    },
    [onClick, setPlayState],
  )

  useEffect(() => {
    switch (playState) {
      case 'playing':
        internalRef.current?.play()
        break
      case 'paused':
        internalRef.current?.pause()
        break
      case 'stopped':
        if (internalRef?.current) {
          internalRef.current.pause()
          internalRef.current.currentTime = 0
        }
    }
  }, [playState])

  return (
    <div className={cx(videoClass, 'video', className)} onClick={onClickInternal}>
      <video ref={mergedRefs} className={cx('video', className)} src={src} controls={true} autoPlay={autoPlay} {...rest}></video>
      <div className={cx('play-indicator', playState)}>
        <Icon className="icon" size="xxl" iconUrl={playState === 'stopped' ? PlayingIcon : PauseIcon} />
      </div>
    </div>
  )
}
